import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import * as Mui from "@mui/material";
import HeadsetMicOutlined from "@mui/icons-material/HeadsetMicOutlined";
import SupportAgentOutlined from "@mui/icons-material/SupportAgentOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import CallOutlined from "@mui/icons-material/CallOutlined";
import MailOutlineRounded from "@mui/icons-material/MailOutlineRounded";
import WhatsApp from "@mui/icons-material/WhatsApp";
import { Loader } from "components/Loader";
import { customGetAPI } from "services/utils/CustomAPI";
import { contactUrl, readContacts } from "pages/SupportAdmin/CauverContact/contactApi";
import {
  designColors,
  designRadii,
  designShell,
  designTheme,
  designType,
} from "services/constants/designTokens";
import {
  GlobalBreadcrumb,
} from "components/DesignSystem/sharedUiPatterns";
import { IconSectionCard, IconTile } from "components/DesignSystem/IconSectionCard";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { withScreenPermissions } from "components/DesignSystem/ScreenAccess";
import { SCREEN_ACCESS } from "services/utils/screenPermissions";
import {
  contactInitials,
  emailHref,
  phoneHref,
} from "./supportContactsData";

const contactLinkSx = {
  minWidth: 0,
  display: "inline-flex",
  alignItems: "center",
  gap: 1,
  fontSize: 14,
  color: designColors.slate900,
  textDecoration: "none",
  overflowWrap: "anywhere",
  "&:hover": { color: designColors.tealDark, textDecoration: "underline" },
};

const actionButtonSx = { minWidth: 120, minHeight: 42 };

const SupportPersonCard = ({ person }) => (
  <Mui.Box
    sx={{
      height: "100%",
      p: { xs: 2, md: 2.5 },
      borderRadius: designRadii.card,
      border: `1px solid ${
        person.isPrimary ? "#B7E4DB" : designColors.slate200
      }`,
      backgroundColor: person.isPrimary
        ? designColors.slateBg
        : designColors.white,
    }}
  >
    <Mui.Stack direction="row" spacing={1.5} alignItems="flex-start">
      <IconTile
        size={44}
        accent={
          person.isPrimary ? detailAccentPalette.green : detailAccentPalette.purple
        }
        icon={
          person.isPrimary ? <SupportAgentOutlined /> : <GroupsOutlined />
        }
      />
      <Mui.Box sx={{ flex: 1, minWidth: 0 }}>
        <Mui.Typography component="h3" sx={designType.calloutTitle}>
          {person.role}
        </Mui.Typography>
        <Mui.Typography sx={designType.calloutText}>
          {person.description}
        </Mui.Typography>
      </Mui.Box>
      <Mui.Stack spacing={1} alignItems="flex-end">
      {person.isPrimary && (
        <RequestStatusChip
          status="Primary Contact"
          colorCode={designColors.tealDark}
          sx={{ flexShrink: 0 }}
        />
      )}
      <RequestStatusChip status={person.active ? "Active" : "Inactive"} colorCode={person.active ? designColors.tealDark : "#6B7280"} />
      </Mui.Stack>
    </Mui.Stack>

    <Mui.Stack
      direction="row"
      spacing={2}
      alignItems="center"
      sx={{ mt: 2.5, pb: 2, borderBottom: `1px solid ${designColors.slate200}` }}
    >
      <Mui.Avatar
        sx={{
          width: 56,
          height: 56,
          fontSize: 18,
          fontWeight: 700,
          bgcolor: designColors.tealTint,
          color: designColors.tealDark,
        }}
      >
        {contactInitials(person.name)}
      </Mui.Avatar>
      <Mui.Box sx={{ minWidth: 0 }}>
        <Mui.Typography sx={{ ...designType.panelTitle, fontSize: 16 }}>
          {person.name}
        </Mui.Typography>
        <Mui.Typography sx={designType.calloutText}>
          {person.designation}
        </Mui.Typography>
      </Mui.Box>
    </Mui.Stack>

    <Mui.Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={{ xs: 1, sm: 3 }}
      useFlexGap
      flexWrap="wrap"
      sx={{ mt: 2 }}
    >
      <Mui.Link href={phoneHref(person.phone)} sx={contactLinkSx}>
        <CallOutlined
          fontSize="small"
          sx={{ color: detailAccentPalette.blue.color }}
        />
        {person.phone}
      </Mui.Link>
      <Mui.Link href={emailHref(person.email)} sx={contactLinkSx}>
        <MailOutlineRounded
          fontSize="small"
          sx={{ color: detailAccentPalette.orange.color }}
        />
        {person.email}
      </Mui.Link>
      {person.whatsapp && <Mui.Link href={`https://wa.me/${person.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" sx={contactLinkSx}>
        <WhatsApp fontSize="small" sx={{ color: designColors.tealDark }} />
        {person.whatsapp}
      </Mui.Link>}
    </Mui.Stack>

    <Mui.Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
      <SecondaryButton
        component="a"
        href={phoneHref(person.phone)}
        startIcon={<CallOutlined />}
        aria-label={`Call ${person.name}`}
        sx={actionButtonSx}
      >
        Call
      </SecondaryButton>
      <SecondaryButton
        component="a"
        href={emailHref(person.email)}
        startIcon={<MailOutlineRounded />}
        aria-label={`Email ${person.name}`}
        sx={actionButtonSx}
      >
        Email
      </SecondaryButton>
    </Mui.Stack>
  </Mui.Box>
);

function SupportContacts() {
  const history = useHistory();
  const { domain } = useSelector((state) => state.tokenReducer);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [showAllContacts, setShowAllContacts] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setContacts([]);
    setShowAllContacts(false);
    const load = async () => {
      try {
        if (!domain) throw new Error("API configuration is unavailable.");
        const result = await customGetAPI(contactUrl(domain));
        if (!result.ok) throw new Error(result.message || "Unable to load support contacts.");
        const rows = readContacts(result.responseDetails);
        if (!cancelled) setContacts(rows.map((contact) => ({
          ...contact,
          isPrimary: contact.contactType === "Primary",
          description: contact.contactType === "Primary" ? "Your main point of contact at Cauver." : "Contact this person for support and assistance.",
        })));
      } catch (loadError) {
        if (!cancelled) setError(loadError.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [domain, retry]);

  if (loading) return <Loader info="Loading..." />;

  return (
    <Mui.ThemeProvider theme={designTheme}>
      <Mui.Box sx={{ ...designShell.pageSurface, p: { xs: 2, md: 3 } }}>
        <Mui.Stack spacing={2}>
          <GlobalBreadcrumb
            trail={[
              {
                label: "Administration",
                onClick: () => history.push("/admin/administration/user"),
              },
              { label: "Support & Contacts" },
            ]}
          />
          <Mui.Box>
            <Mui.Typography component="h1" sx={designType.pageTitle}>
              Support & Contacts
            </Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Contact your Cauver team for support, requirements or any assistance.
            </Mui.Typography>
          </Mui.Box>
          {error && <Mui.Alert severity="error" action={<Mui.Button onClick={() => setRetry((value) => value + 1)}>Retry</Mui.Button>}>{error}</Mui.Alert>}

          <IconSectionCard
            icon={<HeadsetMicOutlined />}
            accent={detailAccentPalette.blue}
            title="Your Cauver Team"
            subtitle="Get in touch with your Cauver team for support, requirements or any assistance."
          >
            <Mui.Grid container spacing={2}>
              {!error && !contacts.length && <Mui.Grid item xs={12}><Mui.Typography sx={designType.calloutText}>No support contacts available.</Mui.Typography></Mui.Grid>}
              {(showAllContacts ? contacts : contacts.slice(0, 2)).map((person) => (
                <Mui.Grid item xs={12} lg={6} key={person.id}>
                  <SupportPersonCard person={person} />
                </Mui.Grid>
              ))}
            </Mui.Grid>
            {contacts.length > 2 && <Mui.Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <Mui.Link component="button" type="button" underline="none" aria-expanded={showAllContacts}
                onClick={() => setShowAllContacts((previous) => !previous)}
                sx={{ color: designColors.tealDark, fontSize: 16, fontWeight: 600, cursor: "pointer" }}>
                {showAllContacts ? "Show less" : `+${contacts.length - 2} more`}
              </Mui.Link>
            </Mui.Box>}
          </IconSectionCard>
        </Mui.Stack>
      </Mui.Box>
    </Mui.ThemeProvider>
  );
}

// Read-only screen (call and email links only): only the view permission
// applies.
export default withScreenPermissions(SupportContacts, SCREEN_ACCESS.supportContacts, "Support & Contacts");
